import React, { useMemo, useState, useEffect } from "react";
import { Select, Button, Tag, Input, Typography, Divider, Avatar } from "antd";
import {
  TeamOutlined,
  UserOutlined,
  AimOutlined,
  ClearOutlined,
  SaveOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;
const { Text } = Typography;

const CATEGORY_ROLE_MAP = {
  "product-quality": ["production manager", "production admin", "sales", "admin"],
  delivery: ["deliveries", "sales", "admin"],
  damaged: ["deliveries", "production admin", "sales", "admin"],
  "wrong-item": ["sales", "deliveries", "admin"],
  "missing-parts": ["production admin", "production manager", "sales", "admin"],
  other: ["admin", "sales", "deliveries", "production manager", "production admin"],
};

const ROLE_COLORS = {
  admin: { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  "super admin": { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  sales: { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
  deliveries: { bg: "#fff7ed", text: "#9a3412", border: "#fed7aa" },
  "production manager": { bg: "#fefce8", text: "#854d0e", border: "#fef08a" },
  "production admin": { bg: "#fff1f2", text: "#9f1239", border: "#fecdd3" },
};

function getRoleStyle(role) {
  return ROLE_COLORS[role?.toLowerCase()] || { bg: "#f8fafc", text: "#475569", border: "#e2e8f0" };
}

export default function ComplaintAssignmentPanel({
  complaint,
  staffList = [],
  loading = false,
  onAssign,
  onClear,
}) {
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [note, setNote] = useState("");

  // ✅ FIX: Sync state when complaint prop changes
  useEffect(() => {
    setSelectedStaffId(complaint?.assignedToUserId || null);
    setNote("");
  }, [complaint?.id, complaint?.assignedToUserId]);

  const recommendedRoles = useMemo(() => {
    return (
      CATEGORY_ROLE_MAP[complaint?.category] || [
        "admin",
        "sales",
        "deliveries",
        "production manager",
        "production admin",
      ]
    );
  }, [complaint?.category]);

  const staffOptions = useMemo(() => {
    return staffList.map((staff) => ({
      value: staff.id,
      label: `${staff.full_name || staff.name || staff.email} — ${staff.role}`,
      role: staff.role,
      name: staff.full_name || staff.name || staff.email,
    }));
  }, [staffList]);

  const handleAssign = async () => {
    try {
      await onAssign?.({
        assignedToUserId: selectedStaffId || null,
        note: note.trim(),
      });
    } catch {
      // handled by parent
    }
  };

  const handleClear = async () => {
    try {
      setSelectedStaffId(null);
      setNote("");
      await onClear?.();
    } catch {
      // handled by parent
    }
  };

  const currentAssignee =
    complaint?.assignee?.name ||
    complaint?.assignee?.full_name ||
    complaint?.assignedRole ||
    null;

  const selectedStaff = staffList.find((s) => s.id === selectedStaffId);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <TeamOutlined style={{ color: "#fff", fontSize: 16 }} />
        </div>
        <div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Ticket Assignment</div>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
            Assign to a staff member
          </div>
        </div>
      </div>

      <div style={{ padding: "18px 20px" }}>
        {/* Ticket Meta Tags */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          {complaint?.category && (
            <Tag
              style={{
                borderRadius: 20,
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                color: "#1d4ed8",
                fontWeight: 500,
              }}
            >
              {complaint.category}
            </Tag>
          )}
          {complaint?.priority && (
            <Tag
              style={{
                borderRadius: 20,
                background:
                  complaint.priority === "urgent"
                    ? "#fff1f2"
                    : complaint.priority === "high"
                    ? "#fff7ed"
                    : "#f0fdf4",
                border: `1px solid ${
                  complaint.priority === "urgent"
                    ? "#fecdd3"
                    : complaint.priority === "high"
                    ? "#fed7aa"
                    : "#bbf7d0"
                }`,
                color:
                  complaint.priority === "urgent"
                    ? "#be123c"
                    : complaint.priority === "high"
                    ? "#c2410c"
                    : "#166534",
                fontWeight: 600,
              }}
            >
              ↑ {complaint.priority}
            </Tag>
          )}
          {complaint?.status && (
            <Tag
              style={{
                borderRadius: 20,
                background:
                  complaint.status === "resolved"
                    ? "#f0fdf4"
                    : complaint.status === "in-progress"
                    ? "#eff6ff"
                    : "#fffbeb",
                border: `1px solid ${
                  complaint.status === "resolved"
                    ? "#bbf7d0"
                    : complaint.status === "in-progress"
                    ? "#bfdbfe"
                    : "#fde68a"
                }`,
                color:
                  complaint.status === "resolved"
                    ? "#166534"
                    : complaint.status === "in-progress"
                    ? "#1d4ed8"
                    : "#92400e",
                fontWeight: 500,
              }}
            >
              {complaint.status === "in-progress"
                ? "In Progress"
                : complaint.status.charAt(0).toUpperCase() + complaint.status.slice(1)}
            </Tag>
          )}
        </div>

        {/* Recommended Roles */}
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "12px 14px",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 8,
              color: "#374151",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <AimOutlined style={{ color: "#3b82f6" }} />
            Recommended Roles
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {recommendedRoles.map((role) => {
              const s = getRoleStyle(role);
              return (
                <span
                  key={role}
                  style={{
                    background: s.bg,
                    border: `1px solid ${s.border}`,
                    color: s.text,
                    borderRadius: 20,
                    padding: "2px 10px",
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {role}
                </span>
              );
            })}
          </div>
        </div>

        {/* Current Assignee */}
        <div
          style={{
            background: currentAssignee ? "#f0fdf4" : "#fafafa",
            border: `1px solid ${currentAssignee ? "#bbf7d0" : "#e2e8f0"}`,
            borderRadius: 12,
            padding: "12px 14px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Avatar
            size={40}
            icon={<UserOutlined />}
            style={{
              background: currentAssignee
                ? "linear-gradient(135deg,#16a34a,#22c55e)"
                : "#e2e8f0",
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
              Current Assignee
            </div>
            <div
              style={{
                fontWeight: 700,
                color: currentAssignee ? "#166534" : "#9ca3af",
                fontSize: 14,
                marginTop: 1,
              }}
            >
              {currentAssignee || "Unassigned"}
            </div>
            {complaint?.assignee?.role && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>{complaint.assignee.role}</div>
            )}
          </div>
          {currentAssignee ? (
            <CheckCircleOutlined style={{ color: "#16a34a", fontSize: 18, flexShrink: 0 }} />
          ) : (
            <ClockCircleOutlined style={{ color: "#d1d5db", fontSize: 18, flexShrink: 0 }} />
          )}
        </div>

        {complaint?.assignedAt && (
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16, marginTop: -10 }}>
            Assigned on {new Date(complaint.assignedAt).toLocaleString()}
          </div>
        )}

        <Divider style={{ margin: "0 0 16px 0" }} />

        {/* Staff Selector */}
        <div style={{ marginBottom: 14 }}>
          <Text strong style={{ display: "block", marginBottom: 6, fontSize: 13, color: "#374151" }}>
            Assign to Staff Member
          </Text>
          <Select
            showSearch
            allowClear
            value={selectedStaffId}
            placeholder="Search by name or role..."
            optionFilterProp="label"
            style={{ width: "100%" }}
            options={staffOptions}
            onChange={(value) => setSelectedStaffId(value || null)}
            optionRender={(option) => {
              const s = getRoleStyle(option.data.role);
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Avatar size={28} icon={<UserOutlined />} style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}`, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{option.data.name}</div>
                    <span style={{ fontSize: 11, background: s.bg, color: s.text, border: `1px solid ${s.border}`, borderRadius: 8, padding: "1px 6px" }}>
                      {option.data.role}
                    </span>
                  </div>
                </div>
              );
            }}
          />
        </div>

        {/* Note */}
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 6, fontSize: 13, color: "#374151" }}>
            Assignment Note <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
          </Text>
          <TextArea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Please follow up with the customer about the delivery issue."
            style={{ borderRadius: 10, fontSize: 13 }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={loading}
            onClick={handleAssign}
            disabled={!selectedStaffId && !complaint?.assignedToUserId}
            style={{
              flex: 1,
              height: 40,
              borderRadius: 10,
              background: "linear-gradient(135deg, #1e40af, #3b82f6)",
              border: "none",
              fontWeight: 600,
            }}
          >
            Save Assignment
          </Button>
          {currentAssignee && (
            <Button
              icon={<ClearOutlined />}
              loading={loading}
              onClick={handleClear}
              danger
              style={{ height: 40, borderRadius: 10, fontWeight: 600 }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
